import { jsxs, jsx } from 'react/jsx-runtime';
import { useRef, useState, useCallback, useEffect } from 'react';
import { IconArrowNarrowLeftSolid, IconArrowNarrowRightSolid } from '@circlefin/euclid-icons/common';
import classNames from 'classnames';
import { Button } from '../Button/Button.es.js';
import { SliderItem } from './Slider.Item/Slider.Item.es.js';

const Slider = ({ className, children, fadeColor = 'gray', itemsToScroll = 1 })=>{
    const arrowContainerRef = useRef(null);
    const itemsContainerRef = useRef(null);
    const [position, setPosition] = useState();
    // how much to scroll by on each arrow click
    // scroll increment = item width * itemsToScroll
    const [scrollIncrement, setScrollIncrement] = useState(100);
    /**
     * Checks which arrows to show.
     */ const checkArrowVisibility = useCallback((el)=>{
        const arrowContainer = arrowContainerRef.current;
        if (arrowContainer) {
            const atLeft = el.scrollLeft <= 0;
            const atRight = el.scrollWidth - el.getBoundingClientRect().width - el.scrollLeft <= 1;
            if (atLeft && !atRight) {
                setPosition('left');
            } else if (atRight && !atLeft) {
                setPosition('right');
            } else if (!atRight && !atLeft) {
                setPosition('middle');
            }
        }
    }, []);
    /**
     * On initial render, we check to see if either of the arrows need to be displayed.
     */ useEffect(()=>{
        if (itemsContainerRef.current) {
            checkArrowVisibility(itemsContainerRef.current);
            // get the first item and set its width as the scroll increment
            const item = itemsContainerRef.current.querySelector('.slider-item');
            if (item) {
                setScrollIncrement(item.getBoundingClientRect().width);
            }
        }
    }, [
        itemsContainerRef,
        checkArrowVisibility
    ]);
    /**
     * Handle container content scroll.
     */ const handleOnScroll = useCallback((e)=>{
        checkArrowVisibility(e.currentTarget);
    }, [
        checkArrowVisibility
    ]);
    /**
     * Handle left arrow click.
     */ const handleArrowLeft = useCallback(()=>{
        const itemsContainer = itemsContainerRef.current;
        if (itemsContainer) {
            itemsContainer.scrollBy({
                left: -(scrollIncrement * itemsToScroll),
                behavior: 'smooth'
            });
        }
    }, [
        scrollIncrement,
        itemsToScroll
    ]);
    /**
     * Handle right arrow click.
     */ const handleArrowRight = useCallback(()=>{
        const itemsContainer = itemsContainerRef.current;
        if (itemsContainer) {
            itemsContainer.scrollBy({
                left: scrollIncrement * itemsToScroll,
                behavior: 'smooth'
            });
        }
    }, [
        scrollIncrement,
        itemsToScroll
    ]);
    return jsxs("div", {
        className: classNames('cb-slider-container', className),
        "data-testid": "slider-container",
        children: [
            jsx("div", {
                ref: itemsContainerRef,
                className: "slider-items-container",
                "data-testid": "items-container",
                onScroll: handleOnScroll,
                children: children
            }),
            jsxs("div", {
                ref: arrowContainerRef,
                className: classNames('slider-arrow-container', {
                    'showing-right-arrow': position === 'left',
                    'showing-left-arrow': position === 'right',
                    'showing-left-and-right-arrow': position === 'middle',
                    'fade-gray': fadeColor === 'gray',
                    'fade-white': fadeColor === 'white'
                }),
                "data-testid": "arrow-container",
                children: [
                    jsx("div", {
                        "aria-hidden": position === 'left',
                        className: "arrow arrow-left",
                        children: jsx(Button, {
                            "aria-hidden": position === 'left',
                            "aria-label": "Previous",
                            className: "arrow-icon",
                            onClick: handleArrowLeft,
                            size: "lg",
                            variant: "primary",
                            children: jsx(IconArrowNarrowLeftSolid, {
                                size: 18
                            })
                        })
                    }),
                    jsx("div", {
                        "aria-hidden": position === 'right',
                        className: "arrow arrow-right",
                        children: jsx(Button, {
                            "aria-hidden": position === 'right',
                            "aria-label": "Next",
                            className: "arrow-icon",
                            onClick: handleArrowRight,
                            size: "lg",
                            variant: "primary",
                            children: jsx(IconArrowNarrowRightSolid, {
                                size: 18
                            })
                        })
                    })
                ]
            })
        ]
    });
};
Slider.Item = SliderItem;
Slider.displayName = 'Slider';

export { Slider, Slider as default };
//# sourceMappingURL=Slider.es.js.map
